import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { iValidatedControl } from '../iValidatedControl';
import { BaseCustomControl } from '../baseCustomControl';
import { CacheService } from '../../../../core/storage/cacheService';
import { StorageService } from '../../../../core/storage/storageService';
import { TaxTypes, UnitsOfMeasureMC } from '../../../../core/constants';
import { AdminService } from '../../../../models/admin/adminService';
import { ListItem } from '../../../../../../../../libs/Shared/components/model/listItem';

export enum DDLModes {
  Default = 'Default',
  States = 'States',
  Countries = 'Countries',
  CCProcessors = 'CCProcessors',
  ACHProcessors = 'ACHProcessors',
  Gateways = 'Gateways',
  Brands = 'Brands',
  TimeZones = 'TimeZones',
  BusinessTypes = 'BusinessTypes',
  UnitsOfMeasure = 'UnitsOfMeasure',
  TaxTypes = 'TaxTypes',
  ProcessingTypes = 'ProcessingTypes',
}

@Component({
  selector: 'app-ddl-input',
  templateUrl: './ddl-input.component.html',
  providers: [
    // see https://blog.thoughtram.io/angular/2016/07/27/custom-form-controls-in-angular-2.html
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DdlInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class DdlInputComponent
  extends BaseCustomControl
  implements ControlValueAccessor, iValidatedControl
{
  @HostBinding('class.formField') formFieldClass = true; // Always applied
  @HostBinding('class.readOnly') get isReadOnly() {
    return this.readonly;
  }

  @Input() align: string = '';
  @Input() options: ListItem[] = [];
  @Input() validationLocation: string = 'right'; // right | under
  @Input() type: DDLModes = DDLModes.Default;
  @Input() showRequired: boolean = false;
  @Input() L2L3Required: boolean = false;
  @Input() multiple: boolean = false;

  @ViewChild('container') container: ElementRef<HTMLSelectElement> | undefined;

  constructor(
    private cache: CacheService,
    private storage: StorageService,
    changeDetectorRef: ChangeDetectorRef,
    private adminService: AdminService,
  ) {
    super(changeDetectorRef);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    if (this.type == DDLModes.Countries) {
      this.cache.getCountriesList().then((l) => {
        this.options = l;
        this.setInitialValue();
      });
    } else if (this.type == DDLModes.CCProcessors) {
      this.options = CacheService.ccProcessorList;
    } else if (this.type == DDLModes.ACHProcessors) {
      this.options = CacheService.achProcessorList;
    } else if (this.type == DDLModes.Gateways) {
      this.options = CacheService.gatewayList;
    } else if (this.type == DDLModes.Brands) {
      //We are doing this here to ensure we have the most up to date list of brands when onboarding a new merchant.
      this.adminService.getMerchantBrands().then((response: string[]) => {
        let brandList = response.map((s) => new ListItem({ id: s, name: s }));
        brandList.push(new ListItem({ id: 'New Brand', name: 'New Brand' }));
        this.storage.setBrandList(brandList);
        this.options = this.storage.getBrandList();
      });
    } else if (this.type == DDLModes.TimeZones) {
      this.options = CacheService.timeZoneList;
    } else if (this.type == DDLModes.BusinessTypes) {
      this.options = CacheService.businessTypesList;
    } else if (this.type == DDLModes.UnitsOfMeasure) {
      this.options = Object.values(UnitsOfMeasureMC).map(
        (u) => new ListItem({ id: u.code, name: u.description }),
      );
    } else if (this.type == DDLModes.TaxTypes) {
      this.options = Object.values(TaxTypes).map(
        (t) => new ListItem({ id: t.code, name: t.description }),
      );
    } else if (this.type == DDLModes.ProcessingTypes) {
      this.options = CacheService.processingTypeList;
    }
  }

  private setInitialValue() {
    if (this.boundValue && this.options) {
      // Ensure the option exists in the dropdown
      const exists = this.options.some((opt) => opt.id === this.boundValue);
      if (!exists) {
        // If the bound value isn't in options, clear it
        this.boundValue = undefined;
      }
    }
  }

  ngAfterViewInit() {
    if (!this.readonly) {
      let c: HTMLSelectElement = document.getElementById(
        this.myId,
      ) as HTMLSelectElement; //document.getElementsByClassName("floatSelect")[0] as HTMLSelectElement;
      c.setAttribute('data-selected-index', '-1');
    }

    // TODO:This causes issues with change detection. If the only point of calling
    // validation here is to force visibility of the required tags, then we
    // should do something different.

    // if (this.showRequired) {
    //   this.validate();
    // }
  }

  get value(): string {
    if (this.boundValue && !this.multiple) {
      return this.boundValue.trim();
    } else if (this.boundValue && this.multiple) {
      return this.boundValue.join(',');
    } else {
      return '';
    }
  }

  public lookupOptions(key: string) {
    if (this.type == DDLModes.States) {
      this.cache.getStatesList(key).then((s) => {
        this.options = s;
      });
    }
  }

  public clearSelection() {
    this.boundValue = undefined;
  }

  public validate(): boolean {
    this.isValid = true;
    this.validationMsg = '';

    if (this.required && this.value == '') {
      this.isValid = false;
      this.validationMsg = 'required';
    } else {
      this.isValid = true;
    }

    if (!this.isValid) {
      this._handle_validateFailed();

      if (this.validationMsg == '') {
        this.validationMsg = 'invalid';
      }
    } else {
      this._handle_validatePassed();
    }

    return this.isValid;
  }

  // private updateSelectedIndex() {
  //   if (this.boundValue && this.boundValue != "") {
  //     for (var i=0; i<this.options.length; i++) {
  //       if (this.options[i].id == this.boundValue) {
  //         this.selectedIndex = i;
  //         break;
  //       }
  //     }
  //   }
  //   else {
  //     this.selectedIndex = -1;
  //   }
  // }

  _handle_change(e: Event) {
    let c: HTMLSelectElement = e.target as HTMLSelectElement;
    c.setAttribute('data-selected-index', c.selectedIndex.toString());

    if (this.validateOn == 'change') {
      this.validate();
    }

    this.onChange(this.boundValue);

    this.onChanged.emit(this.boundValue);

    return true;
  }

  _handle_onBlur(e: FocusEvent) {
    this.validate();
  }
}
